import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import './Dashboard.css';

/**
 * Dashboard Component
 * Demonstrates successful authentication and provides sign-out functionality
 */
export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { authState, signOut } = useAuth();

  /**
   * Handle sign-out button click
   */
  const handleSignOut = async () => {
    try {
      await signOut();
      // Redirect to login page after sign-out
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign-out error:', error);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard__container">
        <header className="dashboard__header">
          <h1 className="dashboard__title">{t('dashboard.heading')}</h1>
          <div className="dashboard__user-info">
            {authState.user && (
              <div className="dashboard__user">
                <span className="dashboard__user-email">
                  {authState.user.email}
                </span>
                <button
                  onClick={handleSignOut}
                  className="dashboard__sign-out"
                  disabled={authState.loading}
                >
                  {authState.loading ? t('dashboard.signingOut') : t('dashboard.signOut')}
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="dashboard__content">
          <div className="dashboard__welcome">
            <h2>{t('dashboard.welcome')}</h2>
            <p>{t('dashboard.authSuccess')}</p>
            
            {authState.user && (
              <div className="dashboard__user-details">
                <h3>{t('dashboard.userInfo')}</h3>
                <dl className="dashboard__user-list">
                  <dt>{t('dashboard.emailLabel')}</dt>
                  <dd>{authState.user.email}</dd>

                  <dt>{t('dashboard.displayNameLabel')}</dt>
                  <dd>{authState.user.displayName || t('dashboard.notProvided')}</dd>

                  <dt>{t('dashboard.emailVerifiedLabel')}</dt>
                  <dd>{authState.user.emailVerified ? t('dashboard.yes') : t('dashboard.no')}</dd>

                  <dt>{t('dashboard.userIdLabel')}</dt>
                  <dd>{authState.user.uid}</dd>

                  <dt>{t('dashboard.lastSignInLabel')}</dt>
                  <dd>{new Date(authState.user.lastSignInAt).toLocaleString()}</dd>
                </dl>
              </div>
            )}

            {authState.platformResponse && (
              <div className="dashboard__platform-info">
                <h3>{t('dashboard.tokenInfo')}</h3>
                <dl className="dashboard__platform-list">
                  <dt>{t('dashboard.tokenExpiresLabel')}</dt>
                  <dd>{new Date(authState.platformResponse.expiresAt).toLocaleString()}</dd>

                  <dt>{t('dashboard.redirectUrlLabel')}</dt>
                  <dd>{authState.platformResponse.redirectUrl}</dd>
                </dl>
              </div>
            )}
          </div>

          {authState.error && (
            <div className="dashboard__error" role="alert">
              <h3>{t('dashboard.error')}</h3>
              <p>{t(authState.error)}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};